import { Chip, Grid, Table, TableBody, TableCell, TableHead, TableRow, Typography } from '@mui/material';
import { FunctionField, Labeled, TextField, useRecordContext, useTranslate } from 'react-admin';

import { FormattedDateField } from 'components/FormattedDateField';
import { UnitSoftwareComponentState, UpdateState } from 'types/Unit';

/**
 * Colour per updater state value as documented by the API:
 * 0 = Idle, 1 = Available, 2 = Downloading, 3 = Restart Required.
 */
const stateColor = (state: UpdateState): 'default' | 'info' | 'warning' | 'error' => {
  switch (state.value) {
    case 1:
      return 'info';
    case 2:
      return 'warning';
    case 3:
      return 'error';
    default:
      return 'default';
  }
};

/**
 * Renders `update_state.label` as computed by the API instead of deriving the
 * wording again on the client.
 */
const UpdateStateChip = ({ state }: { state: UpdateState | null | undefined }) => {
  const translate = useTranslate();

  if (!state) {
    return (
      <Typography component="span" variant="body2" color="text.secondary">
        {translate('resources.units.updater.no_state')}
      </Typography>
    );
  }

  return <Chip label={state.label} size="small" color={stateColor(state)} variant="outlined" />;
};

const SoftwareComponentsTable = ({ components }: { components: UnitSoftwareComponentState[] }) => {
  const translate = useTranslate();

  return (
    <Table size="small">
      <TableHead>
        <TableRow>
          <TableCell>{translate('resources.units.updater.fields.component')}</TableCell>
          <TableCell>{translate('resources.units.updater.fields.version')}</TableCell>
          <TableCell>{translate('resources.units.updater.fields.updating_to')}</TableCell>
          <TableCell>{translate('resources.units.updater.fields.update_state')}</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {components.map((component) => (
          <TableRow key={component.name}>
            <TableCell>
              {/* Reuse the existing per-component labels; the manifest reports dotted names. */}
              {translate(`resources.units.fields.software_versions.${component.name.replace(/\./g, '_')}`, {
                _: component.name,
              })}
            </TableCell>
            <TableCell>{component.version || '-'}</TableCell>
            <TableCell>{component.updating_to || '-'}</TableCell>
            <TableCell>
              <UpdateStateChip state={component.update_state} />
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
};

/**
 * Read-only updater state for a unit (TVR-1508).
 *
 * The unit reports an UpdateStateManifest over MQTT whenever it changes; the API
 * exposes it as `unit.manifest` (release level, `null` until the unit reported
 * one) and `unit.software_components` (per component: installed version, target
 * version and update state).
 */
export const UpdaterStateSection = () => {
  const translate = useTranslate();
  const record = useRecordContext();

  const components: UnitSoftwareComponentState[] = record?.software_components ?? [];
  const manifest = record?.manifest ?? null;

  return (
    <>
      <Typography variant="h6" gutterBottom>
        {translate('resources.units.section.software_update')}
      </Typography>

      {!manifest && components.length === 0 && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {translate('resources.units.updater.never_reported')}
        </Typography>
      )}

      {manifest && (
        <Grid container item spacing={2} columns={{ xs: 1, sm: 4, md: 6 }} sx={{ mb: 2 }}>
          <Grid item xs={1}>
            <Labeled label="resources.units.fields.manifest.update_state">
              <FunctionField render={() => <UpdateStateChip state={manifest.update_state} />} />
            </Labeled>
          </Grid>
          <Grid item xs={1}>
            <Labeled label="resources.units.fields.manifest.current_release_name">
              <TextField source="manifest.current_release_name" emptyText="-" />
            </Labeled>
          </Grid>
          <Grid item xs={1}>
            <Labeled label="resources.units.fields.manifest.new_release_name">
              <TextField source="manifest.new_release_name" emptyText="-" />
            </Labeled>
          </Grid>
          <Grid item xs={1}>
            <Labeled label="resources.units.fields.manifest.update_channel">
              <TextField source="manifest.update_channel" emptyText="-" />
            </Labeled>
          </Grid>
          <Grid item xs={1}>
            <Labeled label="resources.units.fields.manifest.last_update_check_at">
              <FormattedDateField source="manifest.last_update_check_at" />
            </Labeled>
          </Grid>
          <Grid item xs={1}>
            <Labeled label="resources.units.fields.manifest.last_update_received_at">
              <FormattedDateField source="manifest.last_update_received_at" />
            </Labeled>
          </Grid>
        </Grid>
      )}

      {components.length > 0 ? (
        <SoftwareComponentsTable components={components} />
      ) : (
        manifest && (
          <Typography variant="body2" color="text.secondary">
            {translate('resources.units.updater.no_components')}
          </Typography>
        )
      )}
    </>
  );
};
